import i18n from '@/utils/i18n';
import React, { useCallback } from 'react';
import { OrderGuideCollectionData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/sales-portal-order-guide-collections-loader';
import { Pressable } from 'react-native';
import {
  PushView,
  ScrollView,
  Typography,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/contexts/theme-context';

const ROW_MIN_HEIGHT = 56;

interface OrderEntryMobileGuidePickerProps {
  onClose: () => void;
  onSelect: (orderGuide: OrderGuideCollectionData) => void;
  open: boolean;
  orderGuides: OrderGuideCollectionData[];
  selectedOrderGuideUUID: string | undefined;
}

export const OrderEntryMobileGuidePicker: React.FC<
  OrderEntryMobileGuidePickerProps
> = ({ onClose, onSelect, open, orderGuides, selectedOrderGuideUUID }) => {
  const theme = useTheme();
  const insets = useSafeAreaInsets();

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <PushView
      aria-label={i18n.t('order_guide')}
      onOpenChange={handleOpenChange}
      open={open}
    >
      <PushView.Header backLabel={i18n.t('back')}>
        <PushView.Title>{i18n.t('order_guide')}</PushView.Title>
      </PushView.Header>
      <PushView.Body padding="$0">
        <YStack flex={1} minHeight={0}>
          <ScrollView>
            <YStack
              paddingBottom={insets.bottom + theme.sk.spacing.spaceMedium}
              paddingHorizontal="$4"
              paddingTop="$12"
            >
              {orderGuides.map((orderGuide) => {
                const isSelected = orderGuide.uuid === selectedOrderGuideUUID;
                return (
                  <Pressable
                    accessibilityLabel={orderGuide.displayName}
                    accessibilityRole="button"
                    accessibilityState={{ selected: isSelected }}
                    key={orderGuide.uuid}
                    onPress={() => {
                      onSelect(orderGuide);
                      onClose();
                    }}
                  >
                    <YStack
                      backgroundColor={
                        isSelected
                          ? theme.sk.color.bg.secondary.default
                          : 'transparent'
                      }
                      borderRadius="$12"
                      justifyContent="center"
                      minHeight={ROW_MIN_HEIGHT}
                      paddingHorizontal="$12"
                    >
                      <Typography numberOfLines={1} variant="label">
                        {orderGuide.displayName}
                      </Typography>
                    </YStack>
                  </Pressable>
                );
              })}
            </YStack>
          </ScrollView>
        </YStack>
      </PushView.Body>
    </PushView>
  );
};
